// src/features/system/iam-admin/components/UserMembershipsCard.tsx
// ────────────────────────────────────────────────────────────────────────────
// Right pane: shows the selected user's memberships with summary chips
// and a list of MembershipRow children. Owns no fetch (parent supplies
// the MembershipList + mutation callbacks).
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { CARD, TEXT, BADGE, BTN } from '@/shared/dsp/cardTokens';
import FreshnessIndicator from '@/shared/components/ui/FreshnessIndicator';
import MembershipRow from './MembershipRow';
import type {
  MembershipList,
  Membership,
  MembershipStatus,
} from '../api/iamAdminApi';

export interface UserMembershipsCardProps {
  username?:        string | null;
  userRef:          string | null;
  list?:            MembershipList;
  isLoading?:       boolean;
  isFetching?:      boolean;
  isStale?:         boolean;
  isError?:         boolean;
  lastFetchedAt?:   number | null;
  errorMessage?:    string;
  includeRetired:   boolean;
  onToggleRetired:  (v: boolean) => void;
  onAssignClick:    () => void;
  onChangeStatus:   (m: Membership, status: MembershipStatus) => void;
  onToggleDefault:  (m: Membership, next: boolean) => void;
  onRetire:         (m: Membership) => void;
  mutatingRef:      string | null;
  canAssign:        boolean;
}

export default function UserMembershipsCard({
  username, userRef, list, isLoading, isFetching, isStale, isError, lastFetchedAt, errorMessage,
  includeRetired, onToggleRetired, onAssignClick,
  onChangeStatus, onToggleDefault, onRetire, mutatingRef, canAssign,
}: UserMembershipsCardProps) {
  if (!userRef) {
    return (
      <div className={`pac-glass ${CARD.container}`}>
        <div className={`${CARD.section} ${TEXT.emptyState}`}>
          Select a user to view memberships.
        </div>
      </div>
    );
  }

  const summary = list?.summary;
  const memberships = list?.memberships ?? [];

  return (
    <div className={`pac-glass ${CARD.container}`}>
      <div className={CARD.headerSpaced}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={TEXT.primaryBold}>
            Memberships {username ? `· ${username}` : ''}
          </span>
          {summary && (
            <>
              <span className={`${BADGE.base} ${BADGE.neutral}`} title="total">
                total {summary.total}
              </span>
              <span className={`${BADGE.base} ${BADGE.success}`} title="active">
                active {summary.activeCount}
              </span>
              <span className={`${BADGE.base} ${BADGE.violet}`} title="default business">
                default {summary.defaultCount}
              </span>
              {includeRetired && (
                <span className={`${BADGE.base} ${BADGE.neutral}`} title="retired">
                  retired {summary.retiredCount}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[10px] text-neutral-500">
            <input
              type="checkbox"
              checked={includeRetired}
              onChange={e => onToggleRetired(e.target.checked)}
              aria-label="Include retired memberships"
            />
            include retired
          </label>
          <button
            type="button"
            onClick={onAssignClick}
            disabled={!canAssign}
            className={BTN.primary}
            aria-label="Assign new membership"
          >
            + Assign
          </button>
        </div>
      </div>

      {/* Freshness */}
      {(list?.serverTime || lastFetchedAt) && (
        <div className={`${CARD.sectionSm} ${TEXT.dim} flex items-center gap-3`}>
          <FreshnessIndicator
            lastFetchedAt={lastFetchedAt ?? null}
            isFetching={isFetching}
            isStale={isStale}
            isError={isError}
          />
          {list?.serverTime && <span>server time {list.serverTime}</span>}
        </div>
      )}

      <div className={CARD.rowList}>
        {isLoading && (
          <div className={`${CARD.section} ${TEXT.secondary}`}>Loading memberships…</div>
        )}
        {isError && (
          <div className={`${CARD.section} text-xs text-red-400`}>
            Failed to load memberships{errorMessage ? `: ${errorMessage}` : ''}.
          </div>
        )}
        {!isLoading && !isError && memberships.length === 0 && (
          <div className={`${CARD.section} ${TEXT.emptyState}`}>
            No memberships for this user.
          </div>
        )}
        {memberships.map(m => (
          <MembershipRow
            key={m.idMembership}
            m={m}
            isMutating={mutatingRef === m.membershipRef}
            onChangeStatus={(_id, s) => onChangeStatus(m, s)}
            onToggleDefault={(_id, v) => onToggleDefault(m, v)}
            onRetire={() => onRetire(m)}
          />
        ))}
      </div>
    </div>
  );
}
